// @ts-strict-ignore
import * as React from 'react';
import { Mention, MentionsInput, SuggestionDataItem } from 'react-mentions';
import { AllPatternTokens, OrgUser, PatternToken, UserGroup, UserGroupId } from '../../types';
import { displayUserNameLong, displayTextForUserChecklistGroup } from '../../helpers/displayHelpers';
import { antGrey, runwayColors } from '@/components/styles/globalStyles';

const USER_MARKUP = '@{user:__id__|__display__}';
const GROUP_MARKUP = '@{group:__id__|__display__}';
const PATTERN_TOKEN_MARKUP = '{__id__}';

interface Props {
    value?: string;
    onChange?: (next: string) => void;
    onBlur?: () => void;
    placeholder?: string;
    disabled?: boolean;
    rows?: number;
    users: OrgUser[];
    userGroups: Record<UserGroupId, UserGroup>;
    availablePatternTokens?: PatternToken[];
}

export const CustomCopyMentionInput: React.FC<Props> = ({
    value,
    onChange,
    onBlur,
    placeholder,
    disabled,
    rows = 3,
    users,
    userGroups,
    availablePatternTokens,
}) => {
    const userSuggestions: SuggestionDataItem[] = React.useMemo(
        () =>
            users.map((u) => ({
                id: u.email,
                display: displayUserNameLong(u),
            })),
        [users],
    );

    const groupSuggestions: SuggestionDataItem[] = React.useMemo(
        () =>
            Object.values(userGroups).map((g) => ({
                id: g.id,
                display: displayTextForUserChecklistGroup(g),
            })),
        [userGroups],
    );

    const patternTokenSuggestions: SuggestionDataItem[] = React.useMemo(() => {
        const tokens = availablePatternTokens ?? AllPatternTokens;
        return tokens.map((t) => {
            const inner = t.replace(/^\{/, '').replace(/\}$/, '');
            return { id: inner, display: inner };
        });
    }, [availablePatternTokens]);

    return (
        <MentionsInput
            value={value ?? ''}
            onChange={(_e, newValue) => onChange?.(newValue)}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            style={mentionsInputStyle(rows, disabled)}
            allowSpaceInQuery
            a11ySuggestionsListLabel="Suggested mentions"
        >
            <Mention
                trigger="@"
                data={userSuggestions}
                markup={USER_MARKUP}
                displayTransform={(_id, display) => `@${display}`}
                appendSpaceOnAdd
                style={mentionPillStyle}
                renderSuggestion={renderSuggestion}
            />
            <Mention
                trigger="@"
                data={groupSuggestions}
                markup={GROUP_MARKUP}
                displayTransform={(_id, display) => `@${display}`}
                appendSpaceOnAdd
                style={mentionPillStyle}
                renderSuggestion={renderSuggestion}
            />
            <Mention
                trigger="{"
                data={patternTokenSuggestions}
                markup={PATTERN_TOKEN_MARKUP}
                displayTransform={(id) => `{${id}}`}
                appendSpaceOnAdd={false}
                style={patternTokenPillStyle}
                renderSuggestion={renderSuggestion}
            />
        </MentionsInput>
    );
};

const renderSuggestion = (
    suggestion: SuggestionDataItem,
    _search: string,
    highlightedDisplay: React.ReactNode,
    _index: number,
    focused: boolean,
) => (
    <div
        style={{
            padding: '6px 10px',
            backgroundColor: focused ? antGrey.grey3 : 'transparent',
            cursor: 'pointer',
        }}
    >
        {highlightedDisplay}
    </div>
);

const mentionPillStyle: React.CSSProperties = {
    backgroundColor: 'rgba(var(--rw-ant-blue-rgb), 0.18)',
    borderRadius: 3,
    padding: '0 2px',
};

const patternTokenPillStyle: React.CSSProperties = {
    backgroundColor: 'rgba(var(--rw-ant-gold-rgb), 0.22)',
    borderRadius: 3,
    padding: '0 2px',
};

const mentionsInputStyle = (rows: number, disabled: boolean) => ({
    control: {
        fontSize: 14,
        minHeight: rows * 22,
        position: 'relative' as const,
    },
    '&multiLine': {
        control: {
            minHeight: rows * 22,
            position: 'relative' as const,
        },
        highlighter: {
            padding: '4px 11px',
            border: '1px solid transparent',
        },
        input: {
            padding: '4px 11px',
            border: `1px solid ${antGrey.grey5}`,
            borderRadius: 6,
            outline: 'none',
            backgroundColor: disabled ? antGrey.grey2 : runwayColors.white,
            color: disabled ? antGrey.grey6 : runwayColors.black,
            resize: 'none' as const,
        },
    },
    suggestions: {
        position: 'absolute' as const,
        zIndex: 1000,
        marginTop: 4,
        list: {
            backgroundColor: runwayColors.white,
            border: `1px solid ${antGrey.grey5}`,
            borderRadius: 6,
            fontSize: 14,
            maxHeight: 220,
            overflowY: 'auto' as const,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
        item: {
            padding: 0,
        },
    },
});
